import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { AnimalModel } from '../../models/animal.model';
import { AnimalDocumentModel } from '../../models/animal-document.model';
import { AnimalMediaCategoryModel } from '../../models/animal-media-category.model';
import { TokenService } from '../../../../shared/services/token.service';
import { environment } from 'src/environment/environment';
import { ALLOWED_DOCUMENT_ACCEPT, DocumentFileMeta, getDocumentFileMeta, isPreviewableDocument } from '../document-file-type.util';

export type UiAnimalDocument = AnimalDocumentModel & { uiDate?: Date | null };

@Component({
  selector: 'app-documents-grid',
  templateUrl: './documents-grid.component.html',
  styleUrls: ['./documents-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule, FormsModule, TranslocoModule,
    ButtonModule, DialogModule, SelectModule, DatePickerModule, TagModule
  ]
})
export class DocumentsGridComponent {
  @Input() animal: AnimalModel | null = null;
  @Input() documents: UiAnimalDocument[] = [];
  @Input() flippedItems: Record<number, boolean> = {};
  @Input() isMultiSelectMode = false;
  @Input() selectedIds: Set<number> = new Set<number>();
  @Input() categoryOptions: AnimalMediaCategoryModel[] = [];

  @Output() toggleSelect = new EventEmitter<number>();
  @Output() toggleFlip = new EventEmitter<{ id: number, event: MouseEvent }>();
  @Output() categoryChange = new EventEmitter<AnimalDocumentModel>();
  @Output() dateChange = new EventEmitter<UiAnimalDocument>();
  @Output() deleteDocument = new EventEmitter<AnimalDocumentModel>();
  @Output() downloadDocument = new EventEmitter<AnimalDocumentModel>();
  @Output() replaceFile = new EventEmitter<{ document: AnimalDocumentModel, file: File }>();

  previewDialogVisible = false;
  previewUrl: SafeResourceUrl | null = null;
  selectedDocument: UiAnimalDocument | null = null;
  previewAvailable = false;
  readonly acceptTypes = ALLOWED_DOCUMENT_ACCEPT;

  constructor(
    private readonly _tokenService: TokenService,
    private readonly _sanitizer: DomSanitizer
  ) {
  }

  isSelected(id: number): boolean {
    return this.selectedIds.has(id);
  }

  isUpload(document: AnimalDocumentModel): boolean {
    return document.sourceType === 'upload';
  }

  openPreviewDialog(document: UiAnimalDocument, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedDocument = document;
    this.previewAvailable = isPreviewableDocument(document.filePath);
    this.previewUrl = this.previewAvailable
      ? this._sanitizer.bypassSecurityTrustResourceUrl(this.buildFileUrl(document))
      : null;
    this.previewDialogVisible = true;
  }

  closePreviewDialog(): void {
    this.previewDialogVisible = false;
    this.previewUrl = null;
    this.selectedDocument = null;
    this.previewAvailable = false;
  }

  fileMeta(document: AnimalDocumentModel): DocumentFileMeta {
    return getDocumentFileMeta(document.filePath);
  }

  onReplaceFileSelected(document: AnimalDocumentModel, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.replaceFile.emit({ document, file });
    }
    input.value = '';
  }

  fileName(document: AnimalDocumentModel): string {
    return document.filePath?.split('/').pop() ?? document.filePath;
  }

  private buildFileUrl(document: AnimalDocumentModel): string {
    const origin = environment.apiUrl.replace(/\/api\/?$/, '');
    const token = this._tokenService.getAccessToken();
    const url = `${origin}${document.downloadUrl}`;
    return token ? `${url}?x-auth-token=${encodeURIComponent(token)}` : url;
  }
}
